import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { ArrowUpRight, BookOpen, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomeBlogTeaser = () => {
  const { t, lang } = useLang();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/blog/articles`)
      .then((res) => {
        if (mounted) setArticles((res.data || []).slice(0, 3));
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  // Hide the whole section on the homepage until at least one article exists.
  if (loading || articles.length === 0) return null;

  const pickTitle = (a) => (lang === "ar" ? a.title_ar || a.title_en : a.title_en || a.title_ar);
  const pickExcerpt = (a) => (lang === "ar" ? a.excerpt_ar || a.excerpt_en : a.excerpt_en || a.excerpt_ar);
  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB", { year: "numeric", month: "short", day: "numeric" });
    } catch { return ""; }
  };

  return (
    <section className="py-20 md:py-28 bg-[#0d0d10] border-y border-white/5" data-testid="home-blog-teaser">
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="overline mb-4 inline-flex items-center gap-2">
              <BookOpen className="w-3.5 h-3.5" /> {t("home.blogEyebrow")}
            </div>
            <h2 className="display-h2">{t("home.blogTitle")}</h2>
          </div>
          <p className="text-white/60 max-w-md text-base md:text-lg">{t("home.blogSub")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {articles.map((a, i) => (
            <motion.a
              key={a.id}
              href={`/blog/${a.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="group block rounded-xl overflow-hidden border border-white/10 bg-[#0A0A0C] hover:border-[#E6A95B]/40 transition-colors"
              data-testid={`home-blog-card-${a.slug}`}
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-[#0d0d10]">
                {a.cover_image ? (
                  <img
                    src={a.cover_image}
                    alt={pickTitle(a)}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-[1000ms] group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <BookOpen className="w-14 h-14 text-[#E6A95B]/25" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C]/70 to-transparent" />
              </div>
              <div className="p-5 md:p-6">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[#E6A95B]">
                  <Calendar className="w-3 h-3" />
                  <span>{formatDate(a.created_at)}</span>
                </div>
                <h3 className="mt-3 font-display text-xl md:text-2xl leading-tight group-hover:text-[#E6A95B] transition-colors line-clamp-2">
                  {pickTitle(a)}
                </h3>
                {pickExcerpt(a) && (
                  <p className="mt-2 text-white/55 text-sm line-clamp-2">{pickExcerpt(a)}</p>
                )}
                <div className="mt-4 inline-flex items-center gap-1 text-white/80 text-sm font-medium">
                  <span>{t("home.blogReadMore")}</span>
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 -translate-y-0.5 transition" />
                </div>
              </div>
            </motion.a>
          ))}
        </div>

        <div className="mt-14 md:mt-16 flex justify-center">
          <Link
            to="/blog"
            data-testid="home-blog-explore-all"
            className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#E6A95B] text-[#0A0A0C] font-bold text-base md:text-lg hover:brightness-110 hover:shadow-[0_0_40px_-5px_rgba(230,169,91,0.6)] transition-all"
          >
            <span>{t("home.blogExploreAll")}</span>
            <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 -translate-y-0.5 transition" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HomeBlogTeaser;
